import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useList } from '@refinedev/core';
import { BatchScreen } from '@viq-admin/screens';
import type {
  BatchBulkApi,
  BatchConference,
  BatchExportApi,
  BatchLead,
  BatchLeadFieldsApi,
  BatchLeadNotesApi,
  BatchLeadsFetcher,
  BatchLeadsPage,
  BatchMoveApi,
  BatchTagsApi,
  CrmQueueApi,
} from '@viq-admin/screens';
import { useCapability } from '@viq-admin/ui';
import type { Conference, LeadsPage, TagCategory } from '../../api/types';
import { adminFetch } from '../../api/dataProvider';
import { fetchAggregations } from '../../api/batchAggregations';
import { fetchTagDefinitions, addTag, removeTag } from '../../api/tags';
import { setAssignment } from '../../api/assignment';
import { archiveLead, restoreLead } from '../../api/leadRestore';
import {
  downloadExport,
  markExported,
  getColumnPrefs,
  saveColumnPrefs,
} from '../../api/exportWatermark';
import { crmMatchCheck, crmPreview, crmCommit, crmOwners, updateLeadFields } from '../../api/crmUpload';
import { useCrmCapabilities } from '../../hooks/useCrmCapabilities';
import { useCompanyFeatures } from '../../hooks/useCompanyFeatures';
import { listMoveDestinations, moveLeads, previewMove } from '../../api/leadMove';
import { patchLeadFields } from '../../api/leadFields';
import { createRawnote, editRawnote } from '../../api/leadEdit';
import { RestoreLeadButton } from '../../components/RestoreLeadButton';
import { LeadDetailBody } from '../LeadDetail/LeadDetailBody';

// ── Local date grouping (mirrors the shared eventGrouping; conference dates are
//    `YYYY-MM-DD`, which compare correctly as plain strings). Used to reproduce
//    the portal's default-auto-select. ──────────────────────────────────────────
type Group = 'active' | 'upcoming' | 'past';
function todayISO(): string {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}
function groupOf(c: BatchConference, today: string): Group {
  if (!c.startDate || !c.endDate) return 'past';
  if (today < c.startDate) return 'upcoming';
  if (today > c.endDate) return 'past';
  return 'active';
}

const PAGE_SIZE = 50;
function buildLeadsUrl(args: {
  conferenceId: string;
  cursor?: string;
  limit?: number;
  deleted?: boolean;
}): string {
  const params = new URLSearchParams();
  params.set('limit', String(args.limit ?? PAGE_SIZE));
  if (args.deleted) params.set('deleted', 'true');
  // conferenceId is typed `string` but the stakeholder single-pane passes null
  // at runtime — omit the filter in that case so all visible leads return.
  if (args.conferenceId) params.set('conferenceId', args.conferenceId);
  if (args.cursor) params.set('cursor', args.cursor);
  return `/v1/admin/leads?${params.toString()}`;
}

const fetchLeads: BatchLeadsFetcher = (args) =>
  adminFetch<LeadsPage>(buildLeadsUrl(args)) as Promise<BatchLeadsPage>;

/**
 * Editable tag + assignment API wired to the customer plane (`/v1/admin/...`),
 * token-scoped to the caller's company (no companyId). Adapts the portal's
 * `src/api/{tags,assignment}.ts` module shapes to the shared `BatchTagsApi`
 * contract (which speaks `{ tags }` / `{ assignedTo }` envelopes).
 */
const tagsApi: BatchTagsApi = {
  fetchTagDefinitions: async () => ({ definitions: await fetchTagDefinitions() }),
  addTag: (leadId, body) =>
    addTag(leadId, body.category as TagCategory, body.value).then((tags) => ({ tags })),
  removeTag: (leadId, category, value) =>
    removeTag(leadId, category, value).then((tags) => ({ tags })),
  setAssignment: (leadId, assignedTo) =>
    setAssignment(leadId, assignedTo).then((v) => ({ assignedTo: v })),
};

/**
 * The per-lead writes behind the Batch table's row selection. Deliberately the
 * same endpoints the per-row editors already use — the customer plane has no
 * bulk endpoint, so a "bulk" action is N of these, fanned out by the shared
 * screen's `runBulk`. `canManage` is added per render (below) from the role
 * claim; everything else is static.
 */
const bulkActions = {
  addTag: async (leadId: string, body: { category: string; value: string }) => {
    await addTag(leadId, body.category as TagCategory, body.value);
  },
  removeTag: async (leadId: string, category: string, value: string) => {
    await removeTag(leadId, category, value);
  },
  setAssignment: async (leadId: string, assignedTo: string | null) => {
    await setAssignment(leadId, assignedTo);
  },
  archiveLead,
  restoreLead,
};

/**
 * The grid's Notes column (port plan step 5), over the note endpoints the lead
 * drawer already uses: POST /v1/admin/leads/:id/notes for a lead with no note,
 * PATCH …/notes/:noteId with the shown note's `serverVersion` as
 * `expectedVersion` otherwise (409 when someone else saved first). Both answer
 * in `latestNote` shape. A create's `createdAt` is the row's `updated_at`:
 * the insert stamps both with the same now(), and the mapped response keeps
 * only `updatedAt`. An edit leaves `created_at` alone, so the note keeps its
 * own. Static; `BatchRoute` decides per render whether to pass it.
 */
const leadNotesApi: BatchLeadNotesApi = {
  createNote: async (leadId, text) => {
    const saved = await createRawnote(leadId, text);
    return { id: saved.id, text: saved.text, serverVersion: saved.serverVersion, createdAt: saved.updatedAt };
  },
  editNote: async (leadId, note, text) => {
    const saved = await editRawnote(leadId, note.id, { text }, note.serverVersion);
    return { id: saved.id, text: saved.text, serverVersion: saved.serverVersion, createdAt: note.createdAt };
  },
};

/**
 * CSV export API wired to the customer plane. `downloadExport` streams the CSV
 * (server blob) and advances the watermark; `markExported` advances it without a
 * download. xlsx stays a "coming soon" disabled option in the shared dialog.
 */
const exportApi: BatchExportApi = {
  downloadExport: ({
    conferenceId,
    mode,
    format,
    hideColumns,
    includeDiscovery,
    includeDiscoverySummary,
  }) =>
    downloadExport(conferenceId, mode, format, {
      hideColumns,
      includeDiscovery,
      includeDiscoverySummary,
    }),
  markExported: async (conferenceId) => {
    await markExported(conferenceId);
  },
  getColumnPrefs,
  saveColumnPrefs,
};

/**
 * Customer Batch route. Wires the shared Batch workspace to the customer plane's
 * `/v1/admin/*` endpoints, token-scoped (no companyId). Selection is URL-driven
 * (`/batch/:conferenceId`); a 403 on the conferences fetch (stakeholders can't
 * list conferences) drops to the read-only single-pane "Your Leads" view.
 * `crmApi` (the CRM merge queue's Clean/Match/Send flow, p3) is gated
 * fail-closed on `connectedSources` — the stakeholder single-pane branch
 * passes none at all. Nor does it get `bulkApi` (the leads table's row
 * selection) or `moveApi`: that branch is read-only, and every lead mutation
 * on this plane is company_admin-only. `moveApi` (move leads between this
 * company's conferences) is wired only for `manage:Lead` and only when VendoIQ
 * has granted the company the lead-move flag. `leadFieldsApi` (inline editing
 * of Name, Company, Title, Email, Phone and Temp in the grid) is wired only
 * for `manage:Lead`, and never on the stakeholder branch either; nor is
 * `leadNotesApi` (editing the grid's Notes column), nor `unifiedCrmGrid` (the
 * CRM tabs as lenses on the leads grid, port plan step 6).
 */
export function BatchRoute() {
  const { conferenceId } = useParams<{ conferenceId?: string }>();
  const navigate = useNavigate();

  const { result, query } = useList<Conference>({
    resource: 'conferences',
    errorNotification: false, // 403 handled explicitly below
  });

  const conferences = useMemo<BatchConference[]>(
    () =>
      (result?.data ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        location: c.location ?? null,
        startDate: c.startDate ?? undefined,
        endDate: c.endDate ?? undefined,
      })),
    [result?.data],
  );

  // Stakeholder fallback: a 403 on the conferences fetch → single-pane.
  const isStakeholder = useMemo(() => {
    const err = query.error as { statusCode?: number } | null;
    return err != null && err.statusCode === 403;
  }, [query.error]);

  // CRM merge queue (p3 Clean/Match/Send, ADR 019). `connectedSources` is
  // fail-closed ([] on any capability-fetch error); `crmApi` composes the CRM
  // upload endpoints (`../../api/crmUpload.ts`) into the shape `BatchScreen`
  // expects. `canManage` gates the decision controls + Send — the views stay
  // browsable without it (a stakeholder sees everything, disabled).
  const { connectedSources } = useCrmCapabilities();
  const canManageCrm = useCapability('manage:CrmIntegration');
  const crmApi = useMemo<CrmQueueApi>(
    () => ({
      kind: 'crm-upload',
      connectedSources,
      canManage: canManageCrm,
      matchCheck: (confId, source, opts) => crmMatchCheck(confId, source, { force: opts?.force }),
      // Conditional call, not `crmPreview(confId, source, personIds, scope)`
      // unconditionally — an explicit `undefined` 4th argument is NOT the
      // same call shape as omitting the argument (jest's `toHaveBeenCalledWith`
      // treats them as different-length argument lists), and `crmPreview`
      // itself only omits the wire's `scope` key when the argument is
      // genuinely absent. Keeping the call arity conditional here is what
      // makes both match.
      preview: (confId, source, personIds, scope) =>
        scope ? crmPreview(confId, source, personIds, scope) : crmPreview(confId, source, personIds),
      commit: (confId, source, body) => crmCommit(confId, source, body),
      fetchOwners: (source) => crmOwners(source),
      updateLeadFields: (leadId, patch) => updateLeadFields(leadId, patch),
    }),
    [connectedSources, canManageCrm],
  );

  // Bulk lead actions behind the table's row selection. Client gating is
  // UX-only — `adminAuth({ roles: ['company_admin'] })` on each /v1/admin
  // lead endpoint is the gate that matters.
  const canManageLeads = useCapability('manage:Lead');
  const bulkApi = useMemo<BatchBulkApi>(
    () => ({ canManage: canManageLeads, ...bulkActions }),
    [canManageLeads],
  );

  // Inline editing of the grid's contact fields (viq-admin#57). Its presence
  // is what makes the cells editable, so it is withheld entirely without
  // manage:Lead. UX only — `adminAuth({ roles: ['company_admin'] })` on
  // PATCH /v1/admin/leads/:id/fields is the gate that matters.
  const leadFieldsApi = useMemo<BatchLeadFieldsApi | undefined>(
    () => (canManageLeads ? { updateFields: patchLeadFields } : undefined),
    [canManageLeads],
  );

  // The grid's Notes column, editable for the same callers (manage:Lead) and
  // withheld otherwise. UX only — `adminAuth({ roles: ['company_admin'] })` on
  // both note endpoints is the gate that matters.
  const gridNotesApi = canManageLeads ? leadNotesApi : undefined;

  // Move leads between this company's conferences: only for company admins
  // (manage:Lead), and only when VendoIQ has granted the company the feature.
  // Both gates are UX; the backend enforces role + flag on every call.
  // `companyId` is a fixed marker, not a real id: the portal's endpoints are
  // token-scoped, so it only switches the action on (null would read as "no
  // company selected") and keys BatchScreen's selection-reset guard.
  const { leadMove } = useCompanyFeatures();
  const moveApi = useMemo<BatchMoveApi | undefined>(
    () =>
      leadMove && canManageLeads
        ? { companyId: 'own', listDestinations: () => listMoveDestinations(), previewMove, moveLeads }
        : undefined,
    [leadMove, canManageLeads],
  );

  // Auto-select the most recent ACTIVE event when the URL has no
  // `:conferenceId` (replace, not push, so back skips the empty state).
  // Prefer active → most-recently-ended past → soonest upcoming.
  useEffect(() => {
    if (isStakeholder) return;
    if (conferenceId) return;
    if (conferences.length === 0) return;
    const today = todayISO();
    const active = conferences
      .filter((c) => groupOf(c, today) === 'active')
      .sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? ''));
    const past = conferences
      .filter((c) => groupOf(c, today) === 'past')
      .sort((a, b) => (b.endDate ?? '').localeCompare(a.endDate ?? ''));
    const upcoming = conferences
      .filter((c) => groupOf(c, today) === 'upcoming')
      .sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? ''));
    const target = active[0] ?? past[0] ?? upcoming[0];
    if (target) {
      navigate(`/batch/${target.id}`, { replace: true });
    }
  }, [conferenceId, conferences, isStakeholder, navigate]);

  if (isStakeholder) {
    return (
      <BatchScreen
        singlePane
        conferences={[]}
        conferencesLoading={false}
        fetchAggregations={fetchAggregations}
        fetchLeads={fetchLeads}
        tagsApi={tagsApi}
        exportApi={exportApi}
        renderLeadDetail={(id) => <LeadDetailBody id={id} />}
        // No Restore on the Deleted tab: restoring a lead is company_admin
        // only on this plane, so a stakeholder's press could only fail.
        // (Tags and assignment are already read-only here: single-pane
        // renders the table `readOnly`.)
      />
    );
  }

  return (
    <BatchScreen
      // The unified CRM grid (port plan step 6): Clean / Match / Send reuse
      // the leads grid. Only this company-admin branch; the CRM tabs still
      // show only with a connected CRM (`crmApi` below, D4).
      unifiedCrmGrid
      conferences={conferences}
      conferencesLoading={query.isLoading}
      selectedId={conferenceId ?? null}
      onSelect={(id) => navigate(`/batch/${id}`)}
      fetchAggregations={fetchAggregations}
      fetchLeads={fetchLeads}
      tagsApi={tagsApi}
      exportApi={exportApi}
      crmApi={connectedSources.length > 0 ? crmApi : undefined}
      bulkApi={bulkApi}
      moveApi={moveApi}
      leadFieldsApi={leadFieldsApi}
      leadNotesApi={gridNotesApi}
      renderLeadDetail={(id) => <LeadDetailBody id={id} />}
      renderDeletedRowActions={(lead: BatchLead, reload) => (
        <RestoreLeadButton
          id={lead.id}
          displayName={lead.preview ?? lead.id}
          onRestored={reload}
        />
      )}
    />
  );
}
